import {Injectable, Injector} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {
    BpmnProcessCreatorActionTypes,
    GenerateBpmnXml,
    GenerateBpmnXmlFailure,
    GenerateBpmnXmlSuccess,
    PreviewBpmn,
    SaveBpmn,
    SaveBpmnSuccess,
    SaveBpmnXml,
    SaveBpmnXmlFailure,
    SaveBpmnXmlSuccess,
    SaveFailureAction,
    SetBpmnIdentifier,
    ValidateBpmn,
} from '@ispm-client/modules/bpmn-modeler/store/actions';
import {catchError, switchMap, withLatestFrom} from 'rxjs/operators';
import {BpmnDeploymentService} from '@ispm-client/modules/bpmn-modeler/service/bpmn-deployment.service';
import {of} from 'rxjs/observable/of';
import {State} from '@ispm-client/modules/bpmn-modeler/store/state';
import {
    selectBpmnIdentifier,
    selectNodesCreationInfo,
    selectSaveParams
} from '@ispm-client/modules/bpmn-modeler/store/selectors';
import {RulesetUtils} from '@ispm-client/modules/bpmn-modeler/utils/ruleset-utils';
import {get, isEmpty, uniq} from 'lodash';
import {BpmnModelerService} from '@ispm-client/modules/bpmn-modeler/service/bpmn-modeler.service';
import {selectLastNodeId} from '@ispm-client/modules/bpmn-modeler/store/selectors/node-id-generator.selector';
import {BpmnNodeCreatedResult} from '@ispm-client/modules/bpmn-modeler/models/bpmn-node-created-result';
import {NodeCreationResult} from '@ispm-client/modules/bpmn-modeler/models/node-creation-result';
import {TreatmentPlanSelectorResult} from '@ispm-client/modules/bpmn-modeler/models/treatment-plan-selector-result';
import {pathwayBpmnType} from '@ispm-client/modules/pathways';
import {selectFloatedNodesMap} from "@ispm-client/modules/bpmn-modeler/store/selectors/floating-nodes.selector";
import {IgenomicsState} from "@ispm/core/interfaces/genomics-state.interface";
import {CurationSettings} from "../../../../app/curation/components/curation-dashboard/curation-setting";
import {PreviewStatus} from "@ispm/test/services/preview/preview-status";
import {CURATION_ACTIONS} from "../../../../app/curation/store/curation.actions";
import {getPreviewStatus} from "@ispm/test/services/preview/preview.selectors";
import {getActiveKnowledgeBase} from "../../../../app/curation/store/curation.selectors";
import {NavigateToNode} from "@ispm-client/modules/bpmn-modeler/store/actions/node-navigator.action";

@Injectable()
export class SaveBpmnEffect {


  @Effect()
  validateBpmnEffect: Observable<Action> = this.actions$
    .pipe(
      ofType<ValidateBpmn>(BpmnProcessCreatorActionTypes.VALIDATE_BPMN),
      withLatestFrom(this.store$.pipe(select(selectSaveParams))),
      switchMap(([action]) => {
        if (action.payload.curationStatus === CurationSettings.CURATION_STATUS_DRAFT) {
          this.bpmnModelerService.prepareForPreviewBpmn();
          this.bpmnModelerService.prepareForPreview();
          this.bpmnModelerService.bpmnValidation();
          this.genomicsState.dispatch({
            type: CURATION_ACTIONS.SET_PREVIEW_MODE,
            payload: PreviewStatus.start
          });
          return [new GenerateBpmnXml({})];
        } else {
          this.genomicsState.dispatch({
            type: CURATION_ACTIONS.SET_PREVIEW_MODE,
            payload: PreviewStatus.loading
          });
          return [new PreviewBpmn({readyForRun: true})]
        }

      }),
    );

  @Effect()
  saveBpmnEffect: Observable<Action> = this.actions$
    .pipe(
      ofType<SaveBpmn>(BpmnProcessCreatorActionTypes.SAVE_BPMN),
      withLatestFrom(this.store$.pipe(select(selectSaveParams))),
      switchMap(([action, saveParams]) => {
        if (saveParams.deployAsFinal) {
          this.bpmnModelerService.prepareForFinalDeployment();
        }
        if (!saveParams.skipValidation) {
          this.bpmnModelerService.bpmnValidation();
        }
        this.bpmnModelerService.prepareForSave();
        return [new GenerateBpmnXml({onSuccess: saveParams.onSuccess})];
      }),
    );

  @Effect()
  generateBpmnSuccessEffect: Observable<Action> = this.actions$
    .pipe(
      ofType<GenerateBpmnXmlSuccess>(BpmnProcessCreatorActionTypes.GENERATE_BPMN_XML_SUCCESS),
      switchMap(action => [new SaveBpmnXml({
        bpmnXml: action.payload.bpmnXml,
        onSuccess: action.payload.onSuccess
      })])
    );

  @Effect()
  saveBpmnXMLEffect: Observable<Action> = this.actions$
    .pipe(
      ofType<SaveBpmnXml>(BpmnProcessCreatorActionTypes.SAVE_BPMN_XML),
      withLatestFrom(
        this.store$.pipe(select(selectBpmnIdentifier)),
        this.store$.pipe(select(selectSaveParams)),
        this.genomicsState.pipe(select(getPreviewStatus)),
        this.genomicsState.pipe(select(getActiveKnowledgeBase))
      ),
      switchMap(([action, identifier, saveParams, previewStatus,knowledgeBase]) =>
        this.getBpmnDeploymentService().saveBpmnXML(identifier.name, action.payload.bpmnXml, ((!!saveParams && !!saveParams.deployAsFinal) || (previewStatus === PreviewStatus.start)),knowledgeBase ? knowledgeBase.toString() : undefined, pathwayBpmnType)
              .pipe(
                  switchMap((res) => [new SaveBpmnXmlSuccess({ id: res, onSuccess: action.payload.onSuccess })]),
                  catchError(httpError => {
                    this.bpmnModelerService.restoreBpmnBeforeApproval();
                    if (previewStatus === PreviewStatus.start) {
                          this.genomicsState.dispatch({
                              type: CURATION_ACTIONS.SET_PREVIEW_MODE,
                              payload: PreviewStatus.finish
                          });
                      }
                      if (httpError.status === 422) {
                        if (action.payload.bpmnXml) {
                          return this.handleInvalidPathwayError(httpError.error.message);
                        } else {
                          //empty xml, allow archive
                          return of(new SaveBpmnXmlSuccess({id: identifier, onSuccess: action.payload.onSuccess}));
                        }
                      }
                      if(httpError.error){
                          const errorMsg = httpError.error.message ? httpError.error.message : httpError.message;
                          return of(new SaveBpmnXmlFailure({error: errorMsg}));
                      }else {
                          return of(new SaveBpmnXmlFailure({error: httpError.statusText}));
                      }

                  })
          ))
    );

  @Effect()
  saveSuccessEffect: Observable<Action> = this.actions$
    .pipe(
      ofType<SaveBpmnXmlSuccess>(BpmnProcessCreatorActionTypes.SAVE_BPMN_XML_SUCCESS),
      withLatestFrom(this.store$.pipe(select(selectNodesCreationInfo)),
        this.store$.pipe(select(selectLastNodeId)),
        this.store$.pipe(select(selectFloatedNodesMap)),
        this.genomicsState.pipe(select(getPreviewStatus))),
      switchMap(([action, nodesInfo, lastNodeId, floatedNodesMap, previewStatus]) => {
        const nodeSelectionDependencies = this.bpmnModelerService.computeSelectionDependencyMap();
        const fields = this.generateUniqFields(nodesInfo);
        // validate nodes that may be existed in curation item but not existed in bpmn
        nodesInfo = this.bpmnModelerService.validateNodesCreationInfo(nodesInfo);
        const floatedNodes = this.bpmnModelerService.validateFloatingNodes(floatedNodesMap);
        if (previewStatus === PreviewStatus.start) {
          this.genomicsState.dispatch({
            type: CURATION_ACTIONS.SET_PREVIEW_MODE,
            payload: PreviewStatus.loading
          });
          return [new PreviewBpmn({
            bpmnIdentifier: action.payload.id,
            nodes: nodesInfo,
            fields: fields,
            currentCounter: lastNodeId,
            floatedNodes: floatedNodes,
            nodeSelectionDependencies,
            readyForRun: true})]
        } else {
          return [new SetBpmnIdentifier(action.payload.id), new SaveBpmnSuccess({
            data: {
              bpmnIdentifier: action.payload.id,
              nodes: nodesInfo,
              fields: fields,
              currentCounter: lastNodeId,
              floatedNodes: floatedNodes,
              nodeSelectionDependencies,
              readyForRun: true
            },
            onSuccess: action.payload.onSuccess
          })]
        }
      })
    );
  @Effect()
  saveFailureEffect: Observable<Action> = this.actions$
    .pipe(
      ofType<SaveBpmnXmlFailure | GenerateBpmnXmlFailure>(BpmnProcessCreatorActionTypes.SAVE_BPMN_XML_FAILURE, BpmnProcessCreatorActionTypes.GENERATE_BPMN_XML_FAILURE),
      switchMap((action) => [new SaveFailureAction({error: action.payload.error})])
    );

  constructor(private actions$: Actions,
              private injector: Injector,
              private store$: Store<State>,
              private bpmnModelerService: BpmnModelerService,
              private genomicsState: Store<IgenomicsState>,
  ) {

  }

  private getBpmnDeploymentService(): BpmnDeploymentService {
    return this.injector.get(BpmnDeploymentService);
  }

  private generateUniqFields(nodesInfo: Map<string, BpmnNodeCreatedResult<NodeCreationResult | TreatmentPlanSelectorResult>>) {
    let fields = [];
    Object.keys(nodesInfo).forEach(function (key) {
      let data = null;
      try {
        data = get(nodesInfo, `${key}.data`, null);
      } catch (e) {
        // do nothing
      }
      if (data) {
        if (data.manualSelectNode) {
          fields = [...fields, data.manualSelectNode.fullName];
        }
        if (data.ruleset) {
          const newFields = RulesetUtils.extractRulesFields(data.id, data.ruleset);
          fields = [...fields, ...newFields];
        }
      }
    });
    fields = uniq(fields);
    return fields;
  }

  private handleInvalidPathwayError(message: any) {
    let errorMsg = message;
    try {
      const node = message.match(/Node_(\w+)(?!\w)/g); // find a word which start with Node
      if(!isEmpty(node)) {
          // always return only one node in error message
          this.store$.dispatch(new NavigateToNode({'nodeId' : node[0]}));
      } else {
          const result = message.match(/(?:^|\W)ExclusiveGateway_(\w+)(?!\w)/g); // find a word which start with ExclusiveGateway_
          const gatewayIds = result.map(id => id.replace('\'', ''));
          const nodeIds = this.bpmnModelerService.findGatewaysRelatedNode(gatewayIds);
          const nodeNames = nodeIds.map(node => node.businessObject.name || node.businessObject.id);
          errorMsg = `Invalid pathway, please check the following nodes: ${nodeNames.join(', ')}`;
      }

    } catch (e) {
      console.log(`handleInvalidPathwayError - ${e.msg}`);
    }
    return of(new SaveBpmnXmlFailure({error: errorMsg}));
  }
}
